import React from 'react';
import { Box, ButtonCore, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { CF_NEU_BLD, CF_SAV_BLD } from '../../constants';

import './Prompt.css';

/** Available prompt visual styles */
const KINDS = {
  NEUTRAL: 'neutral',
  SAVI: 'savi',
};

/** Available prompt sizes */
const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
};

/** Prompt visual style type */
type PromptKind = (typeof KINDS)[keyof typeof KINDS];

/** Prompt size type */
type PromptSize = (typeof SIZES)[keyof typeof SIZES];

/**
 * Custom class names for different parts of the Prompt component
 */
interface PromptClassNames {
  /** Class name for the content container */
  contentContainerClassName?: string;
  /** Class name for the left icon */
  leftIconClassName?: string;
  /** Class name for the right icon */
  rightIconClassName?: string;
  /** Class name for the children container */
  childrenClassName?: string;
}

/**
 * Props for the Prompt component
 */
interface PromptProps {
  /** Visual style of the prompt. Defaults to 'neutral' */
  kind?: PromptKind;
  /** Size of the prompt. Defaults to 'medium' */
  size?: PromptSize;
  /** Optional title text (only shown for large size) */
  title?: string;
  /** Main text content to display */
  textContent: string;
  /** Whether the prompt is in enabled state */
  isEnabled?: boolean;
  /** Disabled state */
  isDisabled?: boolean;
  /** Click handler function */
  onClick?: (...args: unknown[]) => unknown;
  /** Icon to display on the left (shown for small and large sizes) */
  leftIcon?: React.ReactElement;
  /** Icon to display on the right (shown for small size only) */
  rightIcon?: React.ReactElement;
  /** Tab index for keyboard navigation */
  tabIndex?: number;
  /** Accessibility label */
  ariaLabel?: string;
  /** Additional CSS class name */
  className?: string;
  /** Custom class names for component parts */
  classNames?: PromptClassNames;
  /** Test ID for testing purposes */
  dataTestId?: string;
}

/**
 * A customizable prompt button component with support for different sizes, styles, and icons.
 *
 * Features:
 * - Three sizes: small, medium, large
 * - Two visual styles: neutral, savi
 * - Optional left/right icons based on size
 * - Text truncation with line limits
 * - Accessibility support
 *
 * @param props - The component props
 * @returns The rendered Prompt component or null if invalid configuration
 */
function Prompt({
  kind,
  size,
  title,
  textContent,
  isEnabled,
  isDisabled,
  onClick,
  leftIcon,
  rightIcon,
  tabIndex,
  ariaLabel,
  className,
  classNames,
  dataTestId,
}: PromptProps) {
  const promptClass = cx(
    'Prompt',
    `Prompt-${kind ?? KINDS.NEUTRAL}`,
    `Prompt--${size ?? SIZES.MEDIUM}`,
    isEnabled && `Prompt-${kind ?? KINDS.NEUTRAL}--isEnabled`,
    className
  );

  const leftIconClass = cx(
    'Prompt-leftIcon',
    `Prompt-icons--${size ?? SIZES.MEDIUM}`,
    classNames?.leftIconClassName
  );

  const rightIconClass = cx(
    'Prompt-rightIcon',
    `Prompt-icons--${size ?? SIZES.MEDIUM}`,
    classNames?.rightIconClassName
  );

  const childrenClass = cx('Prompt-children', classNames?.childrenClassName);

  const getTextContentLineLimit = () => {
    if (size === SIZES.SMALL) {
      return 1;
    }

    if (size === SIZES.MEDIUM) {
      return 2;
    }

    return null;
  };

  if (kind === KINDS.SAVI && size !== SIZES.SMALL) {
    return null;
  }

  return (
    <ButtonCore
      type='button'
      tabIndex={tabIndex ?? 0}
      onClick={onClick}
      ariaLabel={ariaLabel}
      className={promptClass}
      dataTestId={dataTestId}
      isDisabled={Boolean(isDisabled)}>
      <Box
        className={cx(
          'Prompt-contentContainer',
          classNames?.contentContainerClassName
        )}>
        {leftIcon && (size === SIZES.SMALL || size === SIZES.LARGE) && (
          <Box className={leftIconClass}>{leftIcon}</Box>
        )}
        {}
        <Box className={childrenClass}>
          {title && size === SIZES.LARGE && (
            <Typography kind='body1-bold' color={CF_NEU_BLD} lineLimit={2}>
              {title}
            </Typography>
          )}
          <Typography
            kind='body2'
            color={CF_SAV_BLD}
            lineLimit={getTextContentLineLimit()}>
            {textContent}
          </Typography>
        </Box>
        {rightIcon && size === SIZES.SMALL && (
          <Box className={rightIconClass}>{rightIcon}</Box>
        )}
      </Box>
    </ButtonCore>
  );
}

Prompt.defaultProps = {
  kind: KINDS.NEUTRAL,
  size: SIZES.MEDIUM,
  title: null,
  isEnabled: false,
  isDisabled: false,
  onClick: () => {},
  leftIcon: null,
  rightIcon: null,
  tabIndex: 0,
  ariaLabel: '',
  className: null,
  classNames: {
    contentContainerClassName: null,
    childrenClassName: null,
    leftIconClassName: null,
    rightIconClassName: null,
  },
  dataTestId: null,
};

export default Prompt;
